import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private themeSubject: BehaviorSubject<'light' | 'dark'> = new BehaviorSubject<'light' | 'dark'>('dark');
  public theme$: Observable<'light' | 'dark'> = this.themeSubject.asObservable();

  constructor(
    private readonly _storage: StorageService,
  ) { }

  /**
   * Initializes the application theme by checking the current theme setting.
   * If no theme is found, it defaults to the 'dark' theme and applies it.
   *
   * @return {void} This method does not return a value.
   */
  public initializeTheme(): void {
    const theme: 'light' | 'dark' | null = this.getCurrentTheme() as 'light' | 'dark' | null;

    if (!theme) {
      this._storage.setItem('theme', 'dark');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
    this.themeSubject.next(theme || 'dark');
  }

  /**
   * Toggles the application's theme between light and dark modes.
   *
   * This method checks the current theme and switches it to the opposite mode.
   * It updates the theme value in the storage and applies the new theme
   * to the document body.
   *
   * @return {void} Does not return a value.
   */
  public toggleTheme(): void {
    const theme: 'light' | 'dark' = this.getCurrentTheme() === 'light' ? 'dark' : 'light';

    this._storage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    this.themeSubject.next(theme);
  }

  public getCurrentTheme(): 'light' | 'dark' | null {
    return this._storage.getItem('theme') as 'light' | 'dark' | null;
  }
}
